// Spec 45 component-behavior — cmp_ds_banner.
//
// Tenant-wide notice (top-of-page strip). Spec 43 §feat_ds_pattern_
// toast_alert distinguishes: toast = transient feedback; alert = inline
// persistent context; banner = TENANT-WIDE state (e.g., "Scheduled
// maintenance in 30min", "Trial expires in 5 days").
//
// Banner sits above the app shell; one banner at a time as a guideline
// (component itself doesn't enforce — that's app shell's job).
//
// 4 severities mirror Alert, but role/live behave differently: banners
// are usually informational + non-interrupting (info/success/warning),
// danger banners reserved for site-down states. We default to
// role='status' + aria-live='polite' for all severities so a banner
// rendering on every page transition doesn't spam screenreaders;
// urgent system-state banners (danger) can override via aria-live='assertive'
// if needed at the consumer layer.

import { forwardRef, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type BannerSeverity = "info" | "success" | "warning" | "danger";

export interface BannerProps extends HTMLAttributes<HTMLDivElement> {
  readonly severity: BannerSeverity;
  readonly icon?: ReactNode;
  readonly title?: string;
  readonly children?: ReactNode;
  /** Optional CTA — wraps consumer-supplied content. Caller controls keyboard semantics. */
  readonly action?: ReactNode;
  readonly onDismiss?: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly dismissLabel?: string;
}

export const Banner = forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      severity,
      icon,
      title,
      action,
      onDismiss,
      dismissLabel = "Dismiss banner",
      children,
      className,
      ...rest
    },
    ref,
  ) => (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      data-component="banner"
      data-severity={severity}
      className={cx("nai-banner", className)}
      {...rest}
    >
      {icon !== undefined && (
        <span data-component="banner-icon" className="nai-banner-icon" aria-hidden>
          {icon}
        </span>
      )}
      <div data-component="banner-content" className="nai-banner-content">
        {title !== undefined && (
          <div data-component="banner-title" className="nai-banner-title">
            {title}
          </div>
        )}
        {children !== undefined && (
          <div data-component="banner-body" className="nai-banner-body">
            {children}
          </div>
        )}
      </div>
      {action !== undefined && (
        <div data-component="banner-action" className="nai-banner-action">
          {action}
        </div>
      )}
      {onDismiss !== undefined && (
        <button
          type="button"
          data-component="banner-dismiss"
          aria-label={dismissLabel}
          className="nai-banner-dismiss"
          onClick={onDismiss}
        >
          ×
        </button>
      )}
    </div>
  ),
);
Banner.displayName = "Banner";
